/*
 * ascii-background.js
 * --------------------
 * Dependency-free "live ASCII" background renderer.
 *
 * Takes a list of images, converts each to ASCII art on a <canvas>, loops
 * through them with crossfades, and applies subtle per-frame motion so the
 * art feels alive (it is redrawn every frame, not a static image).
 *
 * Works two ways:
 *   1. Shopify section  -> reads a JSON config from a child
 *      <script type="application/json" class="ascii-bg__config"> tag.
 *   2. Standalone        -> same JSON tag, or data-* attributes (see demo).
 *
 * No build step, no dependencies. ~7KB unminified.
 */
(function () {
  'use strict';

  var DEFAULTS = {
    images: [],                 // array of image URLs
    charset: ' .:-=+*#%@',      // dark -> light ramp (low brightness first)
    glyphColor: '#E6FF00',      // mono mode glyph colour
    backgroundColor: '#0a0a0a', // backdrop painted behind the glyphs
    colorMode: 'mono',          // 'mono' | 'source'
    cellSize: 10,               // px width of one character cell (smaller = denser)
    padding: 8,                 // % margin (legacy default; per-image `zoom` overrides)
    zoom: null,                 // default fit: 1 = fill, <1 = padded, >1 = zoomed/cropped
    tile: false,                // default: repeat the image across the screen vs fit once
    boldness: 0,                // 0..100, thickens the glyphs
    noise: 0,                   // 0..100, background static/grain + glitch artifacts
    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    fps: 24,                    // render cap
    cycleSeconds: 6,            // seconds each image is shown
    transition: 'fade',         // 'fade' | 'wipe' | 'iris' | 'blinds' | 'dissolve' | 'cycle'
    animationCycle: null,       // array of animation names — one per image, looping
    backgroundFaint: 0,         // 0..1 — faint drifting glyphs in the empty cells
    flow: 0,                    // 0..1 — fluid positional sway of every glyph
    fadeSeconds: 1.4,           // crossfade duration between images
    animation: 'boil',          // boil|flicker|wave|ripple|pulse|twinkle|scan|rain|swirl|plasma|drift|wobble|dotmatrix|none
    animationSpeed: 1,          // multiplier
    animationAmount: 0.18,      // 0..1 strength of the motion
    rotationSpeed: 30,          // deg/sec — spin speed for STL model "images"
    contrast: 1.1,              // >1 punchier
    gamma: 1.0,                 // brightness curve
    invert: false,              // flip the ramp (light bg / dark glyphs)
    opacity: 1,                 // layer opacity
    maxCells: 16000,            // hard cap on cols*rows for performance
    respectReducedMotion: true,
    // ---- interactive reactivity (all opt-in, off by default) -------------
    reactSpotlight: false,      // brighten art under the cursor (flashlight)
    reactRepel: false,          // push glyphs / particles away from the cursor
    reactAttract: false,        // pull glyphs / particles toward the cursor (magnet)
    reactRipple: false,         // click sends an expanding ripple through the art
    reactTrail: false,          // cursor leaves a fading comet trail of ink
    reactShiver: false,         // cells near the cursor jitter / vibrate
    reactHue: false,            // cursor rotates the colour of nearby cells (Source mode)
    reactScroll: false,         // scrolling shifts (parallax) + energises the art
    reactRadius: 160,           // px radius of the cursor effects
    reactStrength: 0.6          // 0..1 intensity of the reactive effects
  };

  // ---- tiny deterministic value-noise (cheap, no allocation) -------------
  function hash(x, y, t) {
    var n = Math.sin(x * 12.9898 + y * 78.233 + t * 0.137) * 43758.5453;
    return n - Math.floor(n); // 0..1
  }

  function easeInOut(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  // Rotate an RGB colour's hue by `a` radians (cheap YIQ-style rotation matrix),
  // used by the reactHue cursor effect. Returns a clamped [r,g,b] array.
  function rotateHue(r, g, b, a) {
    var c = Math.cos(a), s = Math.sin(a);
    var m0 = 0.299 + 0.701 * c + 0.168 * s;
    var m1 = 0.587 - 0.587 * c + 0.330 * s;
    var m2 = 0.114 - 0.114 * c - 0.497 * s;
    var m3 = 0.299 - 0.299 * c - 0.328 * s;
    var m4 = 0.587 + 0.413 * c + 0.035 * s;
    var m5 = 0.114 - 0.114 * c + 0.292 * s;
    var m6 = 0.299 - 0.300 * c + 1.250 * s;
    var m7 = 0.587 - 0.588 * c - 1.050 * s;
    var m8 = 0.114 + 0.886 * c - 0.203 * s;
    var nr = r * m0 + g * m1 + b * m2;
    var ng = r * m3 + g * m4 + b * m5;
    var nb = r * m6 + g * m7 + b * m8;
    return [
      nr < 0 ? 0 : nr > 255 ? 255 : nr,
      ng < 0 ? 0 : ng > 255 ? 255 : ng,
      nb < 0 ? 0 : nb > 255 ? 255 : nb
    ];
  }

  // ---- STL model support ------------------------------------------------
  function isStl(entry) {
    if (entry && entry.kind === 'stl') { return true; }
    var u = (typeof entry === 'string') ? entry : (entry && entry.url) || '';
    return /\.stl(\?|$)/i.test(u);
  }

  // Parse ASCII or binary STL into a flat Float32Array of triangle vertices.
  function parseSTL(buf) {
    var dv = new DataView(buf);
    var binTris = buf.byteLength >= 84 ? dv.getUint32(80, true) : 0;
    var isBinary = buf.byteLength === 84 + binTris * 50 && binTris > 0;
    if (isBinary) {
      var v = new Float32Array(binTris * 9), off = 84, j = 0;
      for (var i = 0; i < binTris; i++) {
        off += 12; // skip the file's face normal
        for (var k = 0; k < 9; k++) { v[j++] = dv.getFloat32(off, true); off += 4; }
        off += 2; // attribute byte count
      }
      return v;
    }
    // ASCII
    var text = new TextDecoder().decode(buf);
    var re = /vertex\s+(-?[\d.eE+]+)\s+(-?[\d.eE+]+)\s+(-?[\d.eE+]+)/g, m, arr = [];
    while ((m = re.exec(text))) { arr.push(+m[1], +m[2], +m[3]); }
    return new Float32Array(arr);
  }

  // Center + uniformly scale to ~[-1,1] and compute per-face normals.
  function prepMesh(v) {
    var n = (v.length / 9) | 0;
    var minx = Infinity, miny = Infinity, minz = Infinity, maxx = -Infinity, maxy = -Infinity, maxz = -Infinity, i;
    for (i = 0; i < v.length; i += 3) {
      if (v[i] < minx) minx = v[i]; if (v[i] > maxx) maxx = v[i];
      if (v[i + 1] < miny) miny = v[i + 1]; if (v[i + 1] > maxy) maxy = v[i + 1];
      if (v[i + 2] < minz) minz = v[i + 2]; if (v[i + 2] > maxz) maxz = v[i + 2];
    }
    var cx = (minx + maxx) / 2, cy = (miny + maxy) / 2, cz = (minz + maxz) / 2;
    var ext = Math.max(maxx - minx, maxy - miny, maxz - minz) || 1, s = 2 / ext;
    var pos = new Float32Array(v.length);
    for (i = 0; i < v.length; i += 3) {
      pos[i] = (v[i] - cx) * s; pos[i + 1] = (v[i + 1] - cy) * s; pos[i + 2] = (v[i + 2] - cz) * s;
    }
    var nrm = new Float32Array(n * 3);
    for (i = 0; i < n; i++) {
      var o = i * 9;
      var ax = pos[o], ay = pos[o + 1], az = pos[o + 2];
      var ex1 = pos[o + 3] - ax, ey1 = pos[o + 4] - ay, ez1 = pos[o + 5] - az;
      var ex2 = pos[o + 6] - ax, ey2 = pos[o + 7] - ay, ez2 = pos[o + 8] - az;
      var nx = ey1 * ez2 - ez1 * ey2, ny = ez1 * ex2 - ex1 * ez2, nz = ex1 * ey2 - ey1 * ex2;
      var len = Math.hypot(nx, ny, nz) || 1;
      nrm[i * 3] = nx / len; nrm[i * 3 + 1] = ny / len; nrm[i * 3 + 2] = nz / len;
    }
    return { pos: pos, nrm: nrm, n: n };
  }

  // ---- 2D photo -> extruded 3D model ------------------------------------
  // "Swap to 3D": rasterise an image (or SVG) to a coarse silhouette mask,
  // then extrude that mask into a slab mesh (front + back caps + side walls)
  // in the same flat-triangle format prepMesh consumes. No dependencies.
  function isExtrude(entry) { return !!(entry && entry.kind === 'extrude'); }

  // Build a binary occupancy mask on a coarse grid. A cell is "solid" when the
  // pixel is opaque enough AND not near-white — so it works for already-cut-out
  // PNG/SVG (alpha) and for raw photos on white (luminance). `cutout` 0..100
  // controls how aggressively light pixels are dropped (0 = keep all opaque).
  function imageToMask(img, cutout) {
    var GRID = 96; // long-edge resolution of the silhouette
    var iw = img.naturalWidth || img.width || 0;
    var ih = img.naturalHeight || img.height || 0;
    if (!iw || !ih) { iw = ih = GRID; } // SVG with no intrinsic size
    var cols, rows;
    if (iw >= ih) { cols = GRID; rows = Math.max(1, Math.round(GRID * ih / iw)); }
    else { rows = GRID; cols = Math.max(1, Math.round(GRID * iw / ih)); }

    var cv = document.createElement('canvas'); cv.width = cols; cv.height = rows;
    var c = cv.getContext('2d', { willReadFrequently: true });
    c.clearRect(0, 0, cols, rows);
    c.drawImage(img, 0, 0, cols, rows);
    var data;
    try { data = c.getImageData(0, 0, cols, rows).data; }
    catch (e) { return null; } // tainted (CORS)

    var lMax = 255 - Math.max(0, Math.min(100, cutout || 0)) * 1.35; // white cutoff
    var aMin = 36;
    var mask = new Uint8Array(cols * rows), solid = 0;
    for (var i = 0; i < cols * rows; i++) {
      if (data[i * 4 + 3] < aMin) { continue; }
      var lum = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
      if (lum <= lMax) { mask[i] = 1; solid++; }
    }
    return solid ? { mask: mask, cols: cols, rows: rows } : null;
  }

  // Extrude a mask into a flat Float32Array of triangle vertices (CCW so face
  // normals point outward — front +z, back -z, walls away from the solid).
  // `thickness` 0..100 ≈ slab depth as a percent of the model's long edge.
  function extrudeMaskToMesh(m, thickness) {
    var mask = m.mask, cols = m.cols, rows = m.rows;
    var hz = (Math.max(1, Math.min(100, thickness || 18)) / 100) * Math.max(cols, rows) / 2;
    var v = [];
    function solid(x, y) { return x >= 0 && y >= 0 && x < cols && y < rows && mask[y * cols + x]; }
    // (a,b,c,d) wound CCW about the intended outward normal -> two triangles
    function quad(ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz) {
      v.push(ax, ay, az, bx, by, bz, cx, cy, cz);
      v.push(ax, ay, az, cx, cy, cz, dx, dy, dz);
    }
    for (var y = 0; y < rows; y++) {
      for (var x = 0; x < cols; x++) {
        if (!mask[y * cols + x]) { continue; }
        var X0 = x, X1 = x + 1;
        var Y0 = rows - y, Y1 = rows - (y + 1); // flip to y-up; Y0 = top edge
        // caps (BL,BR,TR,TL order keeps front facing +z, back facing -z)
        quad(X0, Y1, hz, X1, Y1, hz, X1, Y0, hz, X0, Y0, hz);       // front
        quad(X0, Y1, -hz, X0, Y0, -hz, X1, Y0, -hz, X1, Y1, -hz);   // back
        // side walls only where the neighbour is empty
        if (!solid(x - 1, y)) { quad(X0, Y1, -hz, X0, Y1, hz, X0, Y0, hz, X0, Y0, -hz); } // left  (-x)
        if (!solid(x + 1, y)) { quad(X1, Y1, hz, X1, Y1, -hz, X1, Y0, -hz, X1, Y0, hz); } // right (+x)
        if (!solid(x, y - 1)) { quad(X0, Y0, hz, X1, Y0, hz, X1, Y0, -hz, X0, Y0, -hz); } // top   (+y)
        if (!solid(x, y + 1)) { quad(X0, Y1, -hz, X1, Y1, -hz, X1, Y1, hz, X0, Y1, hz); } // bottom(-y)
      }
    }
    return new Float32Array(v);
  }

  function AsciiBackground(root) {
    this.root = root;
    this.cfg = this._readConfig();
    if (!this.cfg.images.length) { return; } // nothing to render

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'ascii-bg__canvas';
    this.root.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    // offscreen canvas used to sample image brightness/colour into a grid
    this.sample = document.createElement('canvas');
    this.sctx = this.sample.getContext('2d', { willReadFrequently: true });

    this.frames = [];        // per-image { bright:Float32Array, color:Uint8ClampedArray|null, ok:bool }
    this.cols = 0;
    this.rows = 0;
    this.current = 0;
    this.next = 0;
    this.fadeStart = -1;     // timestamp when a crossfade began (-1 = none)
    this.lastDraw = 0;
    this.lastCycle = 0;
    this.running = false;

    // Phones overheat on the continuous full-canvas glyph render — on mobile
    // run at a lower frame rate and a coarser grid (fewer cells → less work).
    this.isMobile = !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
    if (this.isMobile) {
      this.cfg.fps = Math.min(this.cfg.fps, 12);
      this.cfg.maxCells = Math.min(this.cfg.maxCells, 6000);
    }

    this.reduced = this.cfg.respectReducedMotion &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // interactive reactivity state (pointer / scroll / click)
    this.mx = -99999; this.my = -99999; this.pointerIn = false;
    this._scroll = 0; this._scrollBoost = 0; this._lastWin = 0;
    this._ripples = []; this._now = 0; this._trail = [];
    this._reactive = !this.reduced && !!(this.cfg.reactSpotlight ||
      this.cfg.reactRepel || this.cfg.reactAttract || this.cfg.reactRipple ||
      this.cfg.reactTrail || this.cfg.reactShiver || this.cfg.reactHue ||
      this.cfg.reactScroll);

    this._boundResize = this._onResize.bind(this);
    this._boundVisibility = this._onVisibility.bind(this);
    this._boundFrame = this._frame.bind(this);

    this.root.style.opacity = this.cfg.opacity;

    var self = this;
    this._loadImages().then(function () {
      self._resize();
      self.current = self._randIndex();      // start on a random image
      self.played = [self.current];          // history of recently shown images
      window.addEventListener('resize', self._boundResize);
      document.addEventListener('visibilitychange', self._boundVisibility);
      // pause rendering when scrolled out of view (option 3) — this engine
      // runs full-canvas, so animating it off-screen is pure wasted heat
      if (window.IntersectionObserver) {
        self._io = new IntersectionObserver(function (ents) {
          var vis = ents[0] && ents[0].isIntersecting;
          if (vis && !document.hidden) { self._start(); } else { self._stop(); }
        }, { threshold: 0 });
        self._io.observe(self.root);
      }
      self._initReactive();
      self._start();
    });
  }

  // Attach pointer/scroll/click listeners for the interactive effects. Only
  // wired up when at least one reactive option is enabled (and motion is allowed)
  // so shipped pages that don't use it pay nothing.
  AsciiBackground.prototype._initReactive = function () {
    if (!this._reactive) { return; }
    var self = this;
    this._boundPointerMove = function (e) {
      var r = self.root.getBoundingClientRect();
      self.mx = e.clientX - r.left; self.my = e.clientY - r.top;
      self.pointerIn = self.mx >= 0 && self.my >= 0 && self.mx <= r.width && self.my <= r.height;
      // record a comet trail of recent cursor positions for reactTrail
      if (self.cfg.reactTrail && self.pointerIn) {
        var tr = self._trail, last = tr.length ? tr[tr.length - 1] : null;
        if (!last || (self.mx - last.x) * (self.mx - last.x) + (self.my - last.y) * (self.my - last.y) > 36) {
          tr.push({ x: self.mx, y: self.my, t: self._now });
          if (tr.length > 24) { tr.shift(); }
        }
      }
    };
    this._boundPointerDown = function (e) {
      if (!self.cfg.reactRipple) { return; }
      var r = self.root.getBoundingClientRect();
      var x = e.clientX - r.left, y = e.clientY - r.top;
      if (x < 0 || y < 0 || x > r.width || y > r.height) { return; }
      self._ripples.push({ x: x, y: y, t0: self._now, dm: false });
      if (self._ripples.length > 6) { self._ripples.shift(); }
    };
    this._boundScroll = function () {
      var y = window.pageYOffset || 0;
      self._applyScroll(y - self._lastWin); self._lastWin = y;
    };
    this._boundWheel = function (e) { self._applyScroll(e.deltaY); };
    window.addEventListener('pointermove', this._boundPointerMove, { passive: true });
    window.addEventListener('pointerdown', this._boundPointerDown, { passive: true });
    window.addEventListener('scroll', this._boundScroll, { passive: true });
    window.addEventListener('wheel', this._boundWheel, { passive: true });
  };

  // Feed a scroll/wheel delta into the parallax offset + a decaying "energy"
  // boost that briefly intensifies the motion while the page is moving.
  AsciiBackground.prototype._applyScroll = function (delta) {
    if (!this.cfg.reactScroll) { return; }
    this._scroll += delta;
    this._scrollBoost = Math.min(1, this._scrollBoost + Math.abs(delta) * 0.012);
  };

  AsciiBackground.prototype._readConfig = function () {
    var cfg = {};
    var k;
    for (k in DEFAULTS) { if (DEFAULTS.hasOwnProperty(k)) { cfg[k] = DEFAULTS[k]; } }

    var tag = this.root.querySelector('script.ascii-bg__config');
    if (tag) {
      try {
        var json = JSON.parse(tag.textContent);
        for (k in json) {
          if (json.hasOwnProperty(k) && json[k] !== null && json[k] !== '') { cfg[k] = json[k]; }
        }
      } catch (e) { /* ignore malformed config */ }
    }

    // data-* fallbacks / overrides (handy for the standalone demo)
    var d = this.root.dataset;
    if (d.images) { try { cfg.images = JSON.parse(d.images); } catch (e2) { cfg.images = d.images.split(','); } }
    if (d.charset) { cfg.charset = d.charset; }
    if (d.glyphColor) { cfg.glyphColor = d.glyphColor; }
    if (d.backgroundColor) { cfg.backgroundColor = d.backgroundColor; }

    cfg.images = (cfg.images || []).filter(Boolean);
    return cfg;
  };

  // Each image may be a plain URL string or an object { url, tile, zoom }.
  // Per-image tile/zoom override the global defaults.
  AsciiBackground.prototype._normalizeImages = function () {
    var self = this;
    var dz = (this.cfg.zoom != null) ? this.cfg.zoom : (1 - 2 * (this.cfg.padding || 0) / 100);
    var dc = this.cfg.cellSize || 10;
    return (this.cfg.images || []).map(function (e) {
      if (typeof e === 'string') { return { url: e, tile: !!self.cfg.tile, zoom: dz, cellSize: dc, stl: isStl(e), extrude: false }; }
      if (!e || (!e.url && !(e.frames && e.frames.length))) { return null; }
      if (e.frames && e.frames.length) {
        return {
          frames: e.frames, spriteFps: e.fps != null ? e.fps : 12,
          tile: e.tile != null ? !!e.tile : !!self.cfg.tile,
          zoom: e.zoom != null ? e.zoom : dz,
          cellSize: e.cellSize != null ? e.cellSize : dc,
          stl: false, extrude: false
        };
      }
      return {
        url: e.url,
        tile: e.tile != null ? !!e.tile : !!self.cfg.tile,
        zoom: e.zoom != null ? e.zoom : dz,
        cellSize: e.cellSize != null ? e.cellSize : dc,
        stl: isStl(e),
        extrude: isExtrude(e),
        thickness: e.thickness != null ? e.thickness : 18,
        cutout: e.cutout != null ? e.cutout : 55
      };
    }).filter(Boolean);
  };

  AsciiBackground.prototype._loadImages = function () {
    var self = this;
    var meta = this._normalizeImages();
    return Promise.all(meta.map(function (m) {
      if (m.frames) {
        // animated sprite: load every frame, resampled per draw (see _refreshSprite)
        return Promise.all(m.frames.map(function (u) {
          return new Promise(function (res) {
            var im = new Image(); im.crossOrigin = 'anonymous';
            im.onload = function () { res(im); }; im.onerror = function () { res(null); };
            im.src = u;
          });
        })).then(function (imgs) {
          imgs = imgs.filter(Boolean);
          if (!imgs.length) { return null; }
          return { sprite: imgs, spriteFps: m.spriteFps, tile: m.tile, zoom: m.zoom, cellSize: m.cellSize };
        });
      }
      if (m.stl) {
        // 3D model: fetch + parse into a mesh, rendered live each frame
        return fetch(m.url).then(function (r) { return r.arrayBuffer(); })
          .then(function (buf) { return { mesh: prepMesh(parseSTL(buf)), tile: m.tile, zoom: m.zoom, cellSize: m.cellSize }; })
          .catch(function () { return null; });
      }
      return new Promise(function (resolve) {
        var img = new Image();
        img.crossOrigin = 'anonymous'; // required to read pixels from the CDN
        img.onload = function () {
          if (m.extrude) {
            // "Swap to 3D": build a silhouette mask and extrude it into a mesh.
            var mk = imageToMask(img, m.cutout);
            var verts = mk ? extrudeMaskToMesh(mk, m.thickness) : null;
            if (verts && verts.length >= 9) {
              resolve({ mesh: prepMesh(verts), tile: m.tile, zoom: m.zoom, cellSize: m.cellSize });
              return;
            }
            // nothing survived the cutout (or canvas tainted) -> fall back to flat
          }
          resolve({ img: img, tile: m.tile, zoom: m.zoom, cellSize: m.cellSize });
        };
        img.onerror = function () { resolve(null); };
        img.src = m.url;
      });
    })).then(function (items) {
      items = items.filter(Boolean);
      self._images = items.map(function (i) {
        if (i.mesh) { return { mesh: i.mesh }; }
        if (i.sprite) { return { sprite: i.sprite, spriteFps: i.spriteFps }; }
        return i.img;
      });
      self._meta = items.map(function (i) { return { tile: i.tile, zoom: i.zoom, cellSize: i.cellSize }; });
    });
  };

  // Compute a character grid for a given cell size over the w×h preview area.
  AsciiBackground.prototype._computeGrid = function (w, h, cellSize) {
    var cell = Math.max(2, cellSize || this.cfg.cellSize || 10);
    // monospace glyphs are ~2x taller than wide; keep image proportions
    var charAspect = 0.5; // width / height
    var cellW = cell;
    var cellH = cell / charAspect;
    var cols = Math.max(1, Math.floor(w / cellW));
    var rows = Math.max(1, Math.floor(h / cellH));

    // performance guard: scale cells up until under the cap
    while (cols * rows > this.cfg.maxCells) {
      cellW += 1; cellH = cellW / charAspect;
      cols = Math.max(1, Math.floor(w / cellW));
      rows = Math.max(1, Math.floor(h / cellH));
    }
    return { cols: cols, rows: rows, cellW: cellW, cellH: cellH, charAspect: charAspect };
  };

  // Sample one image into its own grid (each image can have its own cell size).
  // Returns a frame { cols, rows, cellW, cellH, bright, color }.
  // tile: repeat the image; zoom: 1 = fill, <1 = padded, >1 = zoomed/cropped.
  AsciiBackground.prototype._sample = function (img, tile, zoom, cellSize) {
    var grid = this._computeGrid(this.cssW, this.cssH, cellSize);
    var cols = grid.cols, rows = grid.rows;
    this.sample.width = cols;
    this.sample.height = rows;
    var s = this.sctx;

    // paint backdrop first so transparent pixels read as the bg colour
    s.fillStyle = this.cfg.backgroundColor;
    s.fillRect(0, 0, cols, rows);

    if (!(zoom > 0)) { zoom = 1; }
    // Character cells are taller than wide, so correct the image aspect into
    // grid-cell units — otherwise the art looks squished.
    var ir = img.width / img.height;
    var eir = ir / grid.charAspect;

    if (tile) {
      var base = Math.min(cols, rows) * 0.5 * zoom;
      var tw = eir >= 1 ? base : base * eir;
      var th = eir >= 1 ? base / eir : base;
      tw = Math.max(2, Math.round(tw)); th = Math.max(2, Math.round(th));
      var tcv = document.createElement('canvas');
      tcv.width = tw; tcv.height = th;
      var tc = tcv.getContext('2d');
      tc.fillStyle = this.cfg.backgroundColor;
      tc.fillRect(0, 0, tw, th);
      tc.drawImage(img, 0, 0, tw, th);
      var pattern = s.createPattern(tcv, 'repeat');
      s.fillStyle = pattern;
      s.fillRect(0, 0, cols, rows);
    } else {
      // contain-fit to the full grid, then scale by zoom (centred; crops if >1)
      var baseW, baseH;
      if (eir > cols / rows) { baseW = cols; baseH = cols / eir; }
      else { baseH = rows; baseW = rows * eir; }
      var dw = baseW * zoom, dh = baseH * zoom;
      s.drawImage(img, (cols - dw) / 2, (rows - dh) / 2, dw, dh);
    }

    var bright = new Float32Array(cols * rows);
    var color = this.cfg.colorMode === 'source' ? new Uint8ClampedArray(cols * rows * 3) : null;
    try {
      var data = s.getImageData(0, 0, cols, rows).data;
      var contrast = this.cfg.contrast, gamma = this.cfg.gamma;
      for (var i = 0; i < cols * rows; i++) {
        var r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2];
        var lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        lum = (lum - 0.5) * contrast + 0.5;          // contrast around mid
        lum = Math.pow(Math.max(0, Math.min(1, lum)), gamma);
        bright[i] = lum;
        if (color) { color[i * 3] = r; color[i * 3 + 1] = g; color[i * 3 + 2] = b; }
      }
    } catch (e) {
      // canvas tainted (CORS) -> fall back to a flat field so we still render
      for (var j = 0; j < cols * rows; j++) { bright[j] = 0.5; }
    }
    return { cols: cols, rows: rows, cellW: grid.cellW, cellH: grid.cellH, bright: bright, color: color };
  };

  AsciiBackground.prototype._rebuildFrames = function () {
    this.frames = [];
    for (var i = 0; i < this._images.length; i++) {
      var m = (this._meta && this._meta[i]) || {};
      var src = this._images[i];
      if (src && src.mesh) {
        // model frame — sampled from a live render, refreshed every draw
        var cv = this._renderModel(src.mesh, 0);
        var fr = this._sample(cv, m.tile, m.zoom, m.cellSize);
        fr.model = src.mesh; fr.tile = m.tile; fr.zoom = m.zoom; fr.cellSize = m.cellSize;
        this.frames.push(fr);
      } else if (src && src.sprite) {
        var sf = this._sample(src.sprite[0], m.tile, m.zoom, m.cellSize);
        sf.sprite = src.sprite; sf.spriteFps = src.spriteFps; sf.spriteIdx = 0;
        sf.tile = m.tile; sf.zoom = m.zoom; sf.cellSize = m.cellSize;
        this.frames.push(sf);
      } else {
        this.frames.push(this._sample(src, m.tile, m.zoom, m.cellSize));
      }
    }
    var ac = this.cfg.animationCycle;
    if (ac && ac.length) {
      for (var ai = 0; ai < this.frames.length; ai++) {
        this.frames[ai].animMode = ac[ai % ac.length];
      }
    }
  };

  // Software-render the mesh (orthographic, flat-shaded, painter's order, back-
  // face culled) to a square offscreen canvas, spun around Y by `angle`.
  AsciiBackground.prototype._renderModel = function (mesh, angle) {
    var size = 260;
    if (!this._modelCanvas) { this._modelCanvas = document.createElement('canvas'); this._modelCanvas.width = this._modelCanvas.height = size; }
    var cv = this._modelCanvas, c = cv.getContext('2d');
    c.fillStyle = this.cfg.backgroundColor; c.fillRect(0, 0, size, size);

    var cosY = Math.cos(angle), sinY = Math.sin(angle);
    var tilt = 0.32, cosX = Math.cos(tilt), sinX = Math.sin(tilt);
    var lx = -0.36, ly = -0.5, lz = 0.79; // light direction (toward viewer, upper-left)
    var cx = size / 2, cy = size / 2, scl = size * 0.4;
    var invert = this.cfg.invert;
    var pos = mesh.pos, nrm = mesh.nrm, n = mesh.n, tris = [];

    function rot(x, y, z) {           // rotate around Y then X
      var x1 = x * cosY + z * sinY, z1 = -x * sinY + z * cosY;
      var y2 = y * cosX - z1 * sinX, z2 = y * sinX + z1 * cosX;
      return [x1, y2, z2];
    }
    for (var i = 0; i < n; i++) {
      var nr = rot(nrm[i * 3], nrm[i * 3 + 1], nrm[i * 3 + 2]);
      if (nr[2] <= 0) { continue; } // backface (normal points away from +z camera)
      var o = i * 9;
      var a = rot(pos[o], pos[o + 1], pos[o + 2]);
      var b = rot(pos[o + 3], pos[o + 4], pos[o + 5]);
      var d = rot(pos[o + 6], pos[o + 7], pos[o + 8]);
      var shade = 0.25 + 0.75 * Math.max(0, nr[0] * lx + nr[1] * ly + nr[2] * lz);
      tris.push({
        ax: cx + a[0] * scl, ay: cy - a[1] * scl,
        bx: cx + b[0] * scl, by: cy - b[1] * scl,
        dx: cx + d[0] * scl, dy: cy - d[1] * scl,
        z: a[2] + b[2] + d[2], shade: shade
      });
    }
    tris.sort(function (p, q) { return p.z - q.z; }); // far first (camera at +z)

    for (var t = 0; t < tris.length; t++) {
      var tr = tris[t];
      var di = 0.3 + 0.7 * tr.shade;            // desired "ink" 0.3..1
      var L = invert ? (1 - di) : di;           // pixel luminance honouring invert
      var vv = Math.round(L * 255);
      c.fillStyle = 'rgb(' + vv + ',' + vv + ',' + vv + ')';
      c.beginPath();
      c.moveTo(tr.ax, tr.ay); c.lineTo(tr.bx, tr.by); c.lineTo(tr.dx, tr.dy); c.closePath();
      c.fill();
    }
    return cv;
  };

  // Re-sample a model frame at the current rotation angle (mutates the frame).
  AsciiBackground.prototype._refreshModel = function (frame, now) {
    var angle = (now / 1000) * (this.cfg.rotationSpeed || 0) * Math.PI / 180;
    var cv = this._renderModel(frame.model, angle);
    var fr = this._sample(cv, frame.tile, frame.zoom, frame.cellSize);
    frame.bright = fr.bright; frame.color = fr.color;
    frame.cols = fr.cols; frame.rows = fr.rows; frame.cellW = fr.cellW; frame.cellH = fr.cellH;
  };

  // Re-sample an animated sprite frame at the current time (mutates the frame).
  AsciiBackground.prototype._refreshSprite = function (frame, now) {
    var n = frame.sprite.length;
    var i = Math.floor(now / (1000 / (frame.spriteFps || 12))) % n;
    if (i === frame.spriteIdx) { return; }   // same cel — nothing to resample
    frame.spriteIdx = i;
    var fr = this._sample(frame.sprite[i], frame.tile, frame.zoom, frame.cellSize);
    frame.bright = fr.bright; frame.color = fr.color;
    frame.cols = fr.cols; frame.rows = fr.rows; frame.cellW = fr.cellW; frame.cellH = fr.cellH;
  };

  AsciiBackground.prototype._randIndex = function () {
    return Math.floor(Math.random() * this.frames.length);
  };

  // Pick the next image at random, never repeating one of the last two shown
  // (so nothing plays twice in a row, nor the one before that).
  AsciiBackground.prototype._pickNext = function () {
    var n = this.frames.length;
    if (n <= 1) { return this.current; }
    var banned = this.played || [];
    var cands = [];
    for (var i = 0; i < n; i++) { if (banned.indexOf(i) === -1) { cands.push(i); } }
    if (!cands.length) { // too few images to honour the rule — just avoid repeating current
      for (var j = 0; j < n; j++) { if (j !== this.current) { cands.push(j); } }
    }
    return cands[Math.floor(Math.random() * cands.length)];
  };

  AsciiBackground.prototype._resize = function () {
    var w = this.root.clientWidth || window.innerWidth;
    var h = this.root.clientHeight || window.innerHeight;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);

    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.cssW = w; this.cssH = h;
    this._rebuildFrames(); // each frame builds its own grid from its cell size
    this._dmDirty = true;  // dot-matrix particles must re-seed to the new pixel layout
  };

  AsciiBackground.prototype._onResize = function () {
    clearTimeout(this._rt);
    var self = this;
    this._rt = setTimeout(function () { self._resize(); }, 150);
  };

  AsciiBackground.prototype._onVisibility = function () {
    if (document.hidden) { this._stop(); } else { this._start(); }
  };

  // Schedule the next frame at the target fps (via setTimeout → rAF) instead
  // of waking every display refresh (60fps) only to skip-draw — that wasted
  // ~36 wake-ups/sec per instance. rAF still aligns the actual paint to vsync.
  AsciiBackground.prototype._schedule = function () {
    var self = this;
    this._timer = setTimeout(function () {
      self._raf = requestAnimationFrame(self._boundFrame);
    }, 1000 / this.cfg.fps);
  };

  AsciiBackground.prototype._start = function () {
    if (this.running) { return; }
    this.running = true;
    this._raf = requestAnimationFrame(this._boundFrame);
  };

  AsciiBackground.prototype._stop = function () {
    this.running = false;
    clearTimeout(this._timer);
    if (this._raf) { cancelAnimationFrame(this._raf); }
  };

  AsciiBackground.prototype._frame = function (now) {
    if (!this.running) { return; }
    this._schedule();
    this._now = now; // exposed so input handlers can timestamp ripples

    var frameInterval = 1000 / this.cfg.fps;
    if (now - this.lastDraw < frameInterval) { return; }
    this.lastDraw = now;

    if (!this.lastCycle) { this.lastCycle = now; }

    // advance the image cycle
    if (!this.reduced && this.frames.length > 1) {
      if (this.cfg.animation === 'dotmatrix') {
        // particle mode: on cycle, retarget the dots to the next image's layout
        if (now - this.lastCycle > this.cfg.cycleSeconds * 1000) {
          this.current = this._pickNext();
          this.played.push(this.current);
          if (this.played.length > 2) { this.played.shift(); }
          this.lastCycle = now;
          this._dmRetarget(this.current, false);
        }
      } else if (this.fadeStart < 0 && now - this.lastCycle > this.cfg.cycleSeconds * 1000) {
        this.next = this._pickNext();
        this.fadeStart = now;
        // pick this transition's style ('cycle' rotates through them)
        var tmodes = ['wipe', 'iris', 'dissolve', 'blinds'];
        var tcfg = this.cfg.transition || 'fade';
        this._tSeq = (this._tSeq || 0) + 1;
        this._tMode = tcfg === 'cycle' ? tmodes[this._tSeq % tmodes.length] : tcfg;
        this._tDir = Math.floor(Math.random() * 4);
      }
      if (this.cfg.animation !== 'dotmatrix' && this.fadeStart >= 0) {
        var fp = (now - this.fadeStart) / (this.cfg.fadeSeconds * 1000);
        if (fp >= 1) {
          this.current = this.next;
          this.played.push(this.current);
          if (this.played.length > 2) { this.played.shift(); } // remember only the last two
          this.fadeStart = -1;
          this.lastCycle = now;
        }
      }
    }

    this._draw(now);
  };

  AsciiBackground.prototype._draw = function (now) {
    var ctx = this.ctx;
    // reactivity housekeeping: age out ripples, bleed off the scroll energy
    if (this._reactive) {
      if (this._ripples.length) {
        this._ripples = this._ripples.filter(function (r) { return now - r.t0 < 1300; });
      }
      if (this._trail.length) {
        this._trail = this._trail.filter(function (p) { return now - p.t < 650; });
      }
      this._scrollBoost *= 0.92;
    }
    // backdrop
    ctx.fillStyle = this.cfg.backgroundColor;
    ctx.fillRect(0, 0, this.cssW, this.cssH);

    if (this.cfg.animation === 'dotmatrix') {
      this._dmStep(now);
    } else {
      var cur = this.frames[this.current];
      if (cur) {
        // centre the character grid — the row/col remainder otherwise shows as
        // a blank band along the bottom/right edges
        var gf = (this.fadeStart >= 0 && this.frames[this.next]) ? this.frames[this.next] : cur;
        ctx.save();
        ctx.translate(
          Math.max(0, (this.cssW - gf.cols * gf.cellW) / 2),
          Math.max(0, (this.cssH - gf.rows * gf.cellH) / 2)
        );
        if (cur.model) { this._refreshModel(cur, now); }          // spin the model
        else if (cur.sprite) { this._refreshSprite(cur, now); }    // advance the sprite
        // Crossfade is a per-cell morph (brightness/colour interpolate in one
        // render pass) — the smooth dissolve. We render on the INCOMING frame's
        // grid, morphing FROM the outgoing frame (mapped onto this grid). Because
        // the fade ends on exactly the incoming frame's native grid + animation
        // phase, the hand-off to the static render is seamless — no jump.
        if (this.fadeStart >= 0) {
          var blend = easeInOut(Math.min(1, (now - this.fadeStart) / (this.cfg.fadeSeconds * 1000)));
          var nf = this.frames[this.next];
          if (nf && nf.model) { this._refreshModel(nf, now); }
          else if (nf && nf.sprite) { this._refreshSprite(nf, now); }
          if (nf) { this._drawFrame(nf, now, cur, 1 - blend); }
          else { this._drawFrame(cur, now, null, 0); }
        } else {
          this._drawFrame(cur, now, null, 0);
        }
        ctx.restore();
      }
    }

    this._drawNoise(now); // optional background static / glitch overlay
  };

  // Sprinkle flickering ASCII grain into the empty background, plus a few short
  // horizontal glitch streaks. Amount is driven by cfg.noise (0..100).
  AsciiBackground.prototype._drawNoise = function (now) {
    var noise = Math.max(0, Math.min(1, (this.cfg.noise || 0) / 100));
    if (noise <= 0) { return; }
    var f = this.frames[this.current];
    if (!f) { return; }
    var ctx = this.ctx, cols = f.cols, rows = f.rows, cw = f.cellW, ch = f.cellH;
    var ramp = this.cfg.charset, rampMax = ramp.length - 1, invert = this.cfg.invert;
    var t = (now / 1000) * (this.cfg.animationSpeed || 1);
    var tk = Math.floor(t * 12);          // flicker rate (static "snow")
    var density = noise * 0.55;           // share of background cells lit at full noise

    ctx.font = (ch * 0.92) + 'px ' + this.cfg.fontFamily;
    ctx.textBaseline = 'top';
    ctx.fillStyle = this.cfg.glyphColor;

    for (var y = 0; y < rows; y++) {
      var py = y * ch;
      for (var x = 0; x < cols; x++) {
        var idx = y * cols + x;
        var ink = invert ? 1 - f.bright[idx] : f.bright[idx];
        if (ink > 0.04) { continue; }     // only the empty background, keep art clean
        var hv = hash(x, y, tk);
        if (hv > density) { continue; }
        var sel = hash(x * 1.7 + 3, y * 0.9 + 1, tk);
        var gi = sel > 0.92 ? rampMax : 1 + Math.floor(sel * Math.max(1, rampMax * 0.6));
        var glyph = ramp.charAt(Math.min(rampMax, gi));
        if (glyph === ' ') { continue; }
        ctx.globalAlpha = 0.2 + 0.8 * (hv / (density || 1));
        ctx.fillText(glyph, x * cw, py);
      }
    }

    // a few flickering horizontal glitch streaks
    var streaks = Math.floor(noise * 5);
    for (var si = 0; si < streaks; si++) {
      var ry = Math.floor(hash(si * 13.1 + 2, 5, tk) * rows);
      var rx = Math.floor(hash(7, si * 7.3 + 1, tk) * cols);
      var len = 2 + Math.floor(hash(si + 1, si + 1, tk) * 16);
      ctx.globalAlpha = 0.25 + 0.5 * noise;
      for (var c2 = 0; c2 < len && rx + c2 < cols; c2++) {
        var ii = ry * cols + (rx + c2);
        var ink2 = invert ? 1 - f.bright[ii] : f.bright[ii];
        if (ink2 > 0.04) { continue; }
        ctx.fillText('=', (rx + c2) * cw, ry * ch);
      }
    }
    ctx.globalAlpha = 1;
  };

  // Render one frame's ASCII. If blendFrame is given, morph each cell toward it
  // by `blend` (0..1), mapping its grid onto this frame's grid.
  AsciiBackground.prototype._drawFrame = function (frame, now, blendFrame, blend) {
    var ctx = this.ctx;
    var cols = frame.cols, rows = frame.rows, cellW = frame.cellW, cellH = frame.cellH;
    var ramp = this.cfg.charset, rampMax = ramp.length - 1;
    var t = (now / 1000) * this.cfg.animationSpeed;
    var anim = this.reduced ? 'none' : (frame.animMode || this.cfg.animation);
    var amp = this.cfg.animationAmount;
    var mono = this.cfg.colorMode !== 'source';
    var invert = this.cfg.invert;
    var bf = blendFrame || null;

    ctx.font = (cellH * 0.92) + 'px ' + this.cfg.fontFamily;
    ctx.textBaseline = 'top';
    if (mono) { ctx.fillStyle = this.cfg.glyphColor; }

    // boldness: overdraw each glyph with a stroke of increasing width
    var bold = Math.max(0, Math.min(1, (this.cfg.boldness || 0) / 100));
    if (bold > 0) {
      ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      ctx.lineWidth = bold * cellW * 0.16;
      if (mono) { ctx.strokeStyle = this.cfg.glyphColor; }
    }

    var band = anim === 'scan' ? ((t * 0.25) % 1) * rows : 0;
    var cx = cols / 2, cy = rows / 2;
    var driftRows = anim === 'drift' ? Math.floor(t * 8) : 0;

    // ---- interactive reactivity (pointer / click / scroll) ---------------
    var spotOn = this._reactive && this.cfg.reactSpotlight && this.pointerIn;
    var repelOn = this._reactive && this.cfg.reactRepel && this.pointerIn;
    var attractOn = this._reactive && this.cfg.reactAttract && this.pointerIn;
    var rippleOn = this._reactive && this.cfg.reactRipple && this._ripples.length > 0;
    var trailOn = this._reactive && this.cfg.reactTrail && this._trail.length > 0;
    var shiverOn = this._reactive && this.cfg.reactShiver && this.pointerIn;
    var hueOn = this._reactive && this.cfg.reactHue && !mono && this.pointerIn;
    var scrollOn = this._reactive && this.cfg.reactScroll;
    var rRad = this.cfg.reactRadius || 160;
    var rStr = (this.cfg.reactStrength != null ? this.cfg.reactStrength : 0.6);
    var mx = this.mx, my = this.my, ripples = this._ripples, trail = this._trail;
    var scrollRows = 0;
    if (scrollOn) { scrollRows = Math.floor(this._scroll * 0.05); amp += Math.min(0.4, this._scrollBoost * 0.5); }

    // faint background: sparse plasma-drifting glyphs in the EMPTY cells so
    // the backdrop isn't dead space — alpha-composited under the artwork
    var flow = this.reduced ? 0 : (this.cfg.flow || 0);
    var faint = this.cfg.backgroundFaint || 0;
    if (faint > 0 && !this.reduced && mono) {
      // the background pattern changes out with each image, like the artwork
      // animations do: clouds -> streaks -> rings -> sweep. During the image
      // fade it MORPHS from the outgoing pattern to the incoming one and
      // flares brighter — synced to the same transition window.
      var bgModes = ['clouds', 'streaks', 'rings', 'sweep'];
      var bgMode = bgModes[this.current % bgModes.length];
      var bgModeIn = null, bgMix = 0;
      if (this.fadeStart >= 0 && this.frames.length > 1) {
        bgMix = Math.min(1, (now - this.fadeStart) / (this.cfg.fadeSeconds * 1000));
        bgModeIn = bgModes[this.next % bgModes.length];
      }
      var bgPv = function (mode, fx, fy) {
        if (mode === 'streaks') {
          return 0.5 + 0.5 * Math.sin(fx * 0.35 - fy * 0.5 - t * 2.2 + hash(fx, 0, 0) * 0.8);
        }
        if (mode === 'rings') {
          var rdx = fx - cx, rdy = fy - cy;
          return 0.5 + 0.5 * Math.sin(Math.sqrt(rdx * rdx + rdy * rdy) * 0.3 - t * 1.8);
        }
        if (mode === 'sweep') {
          var adx = fx - cx, ady = fy - cy;
          return 0.5 + 0.5 * Math.sin(Math.atan2(ady, adx) * 3 + t * 1.6 + Math.sqrt(adx * adx + ady * ady) * 0.08);
        }
        return (Math.sin(fx * 0.11 + t * 1.1) + Math.sin(fy * 0.13 - t * 0.9) + Math.sin((fx + fy) * 0.07 + t * 0.6) + 3) / 6;
      };
      // flare: the whole field brightens through the middle of a transition
      var bgBaseA = Math.min(1, faint * (1 + (bgModeIn ? 0.5 * Math.sin(bgMix * Math.PI) : 0)));
      for (var fy = 0; fy < rows; fy++) {
        for (var fx = 0; fx < cols; fx++) {
          var fb = frame.bright[fy * cols + fx];
          var fink = invert ? 1 - fb : fb;
          if (fink > 0.06) { continue; } // artwork cells stay crisp
          var pv = bgPv(bgMode, fx, fy);
          if (bgModeIn) {
            // the background crosses over along the SAME transition edge as
            // the artwork (wipe/iris/blinds/dissolve) — one unified sweep
            var bgp = easeInOut(bgMix);
            var boff = null;
            if (this._tMode === 'wipe') {
              boff = this._tDir === 0 ? fx / cols : this._tDir === 1 ? 1 - fx / cols
                   : this._tDir === 2 ? fy / rows : 1 - fy / rows;
            } else if (this._tMode === 'iris') {
              var bdx = fx / cols - 0.5, bdy = fy / rows - 0.5;
              boff = Math.sqrt(bdx * bdx + bdy * bdy) * 1.42;
            } else if (this._tMode === 'blinds') {
              boff = (fx % 12) / 12;
            } else if (this._tMode === 'dissolve') {
              boff = hash(fx, fy, 0);
            }
            var blp = bgp;
            if (boff != null) {
              var bsoft = 0.35;
              blp = (bgp * (1 + bsoft) - boff) / bsoft;
              blp = blp < 0 ? 0 : blp > 1 ? 1 : blp;
            }
            pv = pv * (1 - blp) + bgPv(bgModeIn, fx, fy) * blp;
          }
          if (pv < 0.52) { continue; } // pattern with body, still airy
          var fch = ramp.charAt(1 + Math.min(3, Math.round((pv - 0.52) * 9)));
          if (fch === ' ') { continue; }
          // glyphs near the pattern threshold fade in/out instead of popping
          var ga = bgBaseA * Math.min(1, (pv - 0.52) * 8);
          if (ga < 0.03) { continue; }
          ctx.globalAlpha = ga;
          // the field itself drifts — glyphs swim instead of sitting on the grid
          var bxo = Math.sin(fy * 0.23 + t * 1.2) * cellW * (2 + flow * 2);
          var byo = Math.cos(fx * 0.19 + t * 0.9) * cellH * (2 + flow * 2);
          ctx.fillText(fch, fx * cellW + bxo, fy * cellH + byo);
        }
      }
      ctx.globalAlpha = 1;
    }

    for (var y = 0; y < rows; y++) {
      var py = y * cellH;
      for (var x = 0; x < cols; x++) {
        // displacement animations sample from a shifted position:
        //  'drift' scrolls vertically, 'wobble' shears horizontally per row.
        var sy = anim === 'drift' ? (((y + driftRows) % rows) + rows) % rows : y;
        if (scrollOn) { sy = (((sy + scrollRows) % rows) + rows) % rows; }
        var sx = x;
        if (anim === 'wobble') {
          sx = x + Math.round(Math.sin(y * 0.18 + t * 1.6) * amp * 22);
          sx = sx < 0 ? 0 : sx >= cols ? cols - 1 : sx;
        }
        var idx = sy * cols + sx;
        var b = frame.bright[idx];

        // morph toward the next frame, mapping its (possibly different) grid here
        var bidx = -1;
        var cellBlend = blend;
        var tEdge = 0;
        if (bf) {
          var bx = (x * bf.cols / cols) | 0; if (bx >= bf.cols) { bx = bf.cols - 1; }
          var by = (sy * bf.rows / rows) | 0; if (by >= bf.rows) { by = bf.rows - 1; }
          bidx = by * bf.cols + bx;
          // directional/patterned transitions: each cell crosses over at its
          // own offset so the incoming image sweeps/dissolves across the frame
          if (this._tMode && this._tMode !== 'fade') {
            var gp = 1 - blend; // incoming progress 0..1
            var off;
            if (this._tMode === 'wipe') {
              off = this._tDir === 0 ? x / cols : this._tDir === 1 ? 1 - x / cols
                  : this._tDir === 2 ? y / rows : 1 - y / rows;
            } else if (this._tMode === 'iris') {
              var odx = x / cols - 0.5, ody = y / rows - 0.5;
              off = Math.sqrt(odx * odx + ody * ody) * 1.42;
            } else if (this._tMode === 'blinds') {
              off = (x % 12) / 12;
            } else { // dissolve
              off = hash(x, y, 0);
            }
            var soft = 0.35;
            var lp = (gp * (1 + soft) - off) / soft;
            lp = lp < 0 ? 0 : lp > 1 ? 1 : lp;
            cellBlend = 1 - lp;
            // cells mid-crossover form the transition's leading edge
            if (lp > 0 && lp < 1) { tEdge = 1 - Math.abs(2 * lp - 1); }
          }
          b = b + (bf.bright[bidx] - b) * cellBlend;
        }

        var ink = invert ? 1 - b : b;

        // Only animate cells that already contain artwork (ink above a small
        // floor) so the empty background stays clean.
        if (anim !== 'none' && anim !== 'drift' && anim !== 'wobble' && ink > 0.03) {
          if (anim === 'boil') {
            ink += (hash(x, y, Math.floor(t * 6)) - 0.5) * amp;
          } else if (anim === 'flicker') {
            ink += (hash(x, y, Math.floor(t * 14)) - 0.5) * amp * 1.7;
          } else if (anim === 'wave') {
            ink += Math.sin((x * 0.25) + (y * 0.15) - t * 2) * amp * 0.5;
          } else if (anim === 'ripple') {
            ink += Math.sin(Math.sqrt((x - cx) * (x - cx) + (y - cy) * (y - cy)) * 0.35 - t * 3) * amp * 0.6;
          } else if (anim === 'pulse') {
            ink += Math.sin(t * 2) * amp * 0.7;
          } else if (anim === 'twinkle') {
            if (ink > 0.5) { ink += (hash(x, y, Math.floor(t * 10)) - 0.3) * amp * 1.8; }
          } else if (anim === 'scan') {
            var dist = Math.abs(y - band);
            if (dist < 2) { ink += amp * (1 - dist / 2); }
          } else if (anim === 'rain') {
            // bright streaks falling down each column (per-column random phase)
            ink += Math.sin(y * 0.55 - t * 6 + hash(x, 0, 0) * 6.283) * amp * 0.7;
          } else if (anim === 'swirl') {
            var wdx = x - cx, wdy = y - cy;
            ink += Math.sin(Math.atan2(wdy, wdx) * 4 + Math.sqrt(wdx * wdx + wdy * wdy) * 0.12 - t * 2) * amp * 0.6;
          } else if (anim === 'plasma') {
            ink += (Math.sin(x * 0.2 + t) + Math.sin(y * 0.18 - t * 1.2) + Math.sin((x + y) * 0.13 + t * 0.7)) * amp * 0.27;
          }
        }

        // reactive cursor spotlight: brighten cells near the pointer (flashlight)
        if (spotOn) {
          var spdx = (x * cellW + cellW * 0.5) - mx, spdy = (py + cellH * 0.5) - my;
          var spdd = Math.sqrt(spdx * spdx + spdy * spdy);
          if (spdd < rRad) { ink += rStr * (1 - spdd / rRad) * 0.9; }
        }
        // reactive click ripples: an expanding ring of brightness from each click
        if (rippleOn) {
          var ccx = x * cellW + cellW * 0.5, ccy = py + cellH * 0.5;
          for (var rpi = 0; rpi < ripples.length; rpi++) {
            var rp = ripples[rpi];
            var rage = (now - rp.t0) / 1000; if (rage < 0 || rage > 1.3) { continue; }
            var rdx = ccx - rp.x, rdy = ccy - rp.y;
            var rdd = (Math.sqrt(rdx * rdx + rdy * rdy) - rage * 640) / 30;
            ink += rStr * Math.exp(-rdd * rdd) * (1 - rage / 1.3);
          }
        }
        // reactive comet trail: brighten cells near recent cursor positions,
        // each fading with age so a glowing tail follows the pointer
        if (trailOn) {
          var tcx = x * cellW + cellW * 0.5, tcy = py + cellH * 0.5;
          var trad = rRad * 0.6;
          for (var tpi = 0; tpi < trail.length; tpi++) {
            var tp = trail[tpi];
            var tdx = tcx - tp.x, tdy = tcy - tp.y;
            var tdd = Math.sqrt(tdx * tdx + tdy * tdy);
            if (tdd < trad) {
              var tfade = 1 - (now - tp.t) / 650;
              if (tfade > 0) { ink += rStr * (1 - tdd / trad) * tfade * 0.85; }
            }
          }
        }

        if (ink <= 0.04) { continue; } // skip empty cells (also faster)
        // a bright energy band rides the wipe/iris/dissolve crossover edge
        if (tEdge > 0) { ink += tEdge * 0.55; }
        ink = ink < 0 ? 0 : ink > 1 ? 1 : ink;

        var ch = ramp.charAt(Math.round(ink * rampMax));
        if (ch === ' ') { continue; }

        if (!mono) {
          var col = frame.color;
          var cr = col[idx * 3], cg = col[idx * 3 + 1], cb = col[idx * 3 + 2];
          if (bf && bf.color && bidx >= 0) {
            var nc = bf.color;
            cr = cr + (nc[bidx * 3] - cr) * cellBlend;
            cg = cg + (nc[bidx * 3 + 1] - cg) * cellBlend;
            cb = cb + (nc[bidx * 3 + 2] - cb) * cellBlend;
          }
          // reactive hue rotation: spin the colour of cells near the cursor
          if (hueOn) {
            var hdx = (x * cellW + cellW * 0.5) - mx, hdy = (py + cellH * 0.5) - my;
            var hdd = Math.sqrt(hdx * hdx + hdy * hdy);
            if (hdd < rRad) {
              var rot = rStr * (1 - hdd / rRad) * 2.6; // radians of hue spin
              var rc = rotateHue(cr, cg, cb, rot);
              cr = rc[0]; cg = rc[1]; cb = rc[2];
            }
          }
          var rgb = 'rgb(' + (cr | 0) + ',' + (cg | 0) + ',' + (cb | 0) + ')';
          ctx.fillStyle = rgb;
          if (bold > 0) { ctx.strokeStyle = rgb; }
        }
        var px = x * cellW, dyDraw = py;
        // fluid flow: the whole grid sways on smooth offset fields
        if (flow > 0) {
          px += Math.sin(y * 0.14 + t * 1.1) * cellW * flow * 1.2;
          dyDraw += Math.sin(x * 0.12 - t * 0.9) * cellH * flow * 0.8;
        }
        // reactive cursor repel: shove glyphs away from the pointer
        if (repelOn) {
          var rpx = (px + cellW * 0.5) - mx, rpy = (dyDraw + cellH * 0.5) - my;
          var rpd = Math.sqrt(rpx * rpx + rpy * rpy);
          if (rpd < rRad && rpd > 0.01) {
            var pf = (1 - rpd / rRad); pf = pf * pf * rRad * 0.55 * (0.4 + rStr);
            px += (rpx / rpd) * pf; dyDraw += (rpy / rpd) * pf;
          }
        }
        // reactive cursor attract: pull glyphs toward the pointer (magnet)
        if (attractOn) {
          var apx = (px + cellW * 0.5) - mx, apy = (dyDraw + cellH * 0.5) - my;
          var apd = Math.sqrt(apx * apx + apy * apy);
          if (apd < rRad && apd > 0.01) {
            // scales honestly from zero — strength 0 = no pull (was 0.4 floor)
            var af = (1 - apd / rRad); af = af * af * rRad * 0.5 * rStr;
            px -= (apx / apd) * af; dyDraw -= (apy / apd) * af;
          }
        }
        // reactive shiver: cells near the pointer vibrate a little each frame
        if (shiverOn) {
          var sdx = (px + cellW * 0.5) - mx, sdy = (dyDraw + cellH * 0.5) - my;
          var sdd = Math.sqrt(sdx * sdx + sdy * sdy);
          if (sdd < rRad) {
            var sf = (1 - sdd / rRad) * rStr * cellW * 0.5;
            px += (hash(x, y, Math.floor(t * 40)) - 0.5) * sf;
            dyDraw += (hash(x, y, Math.floor(t * 40) + 7) - 0.5) * sf;
          }
        }
        ctx.fillText(ch, px, dyDraw);
        if (bold > 0) { ctx.strokeText(ch, px, dyDraw); }
      }
    }
  };

  // ===== Dot-matrix particle mode ========================================
  // Each lit cell of the current image is a particle (a glyph). On an image
  // change the particles spring to the new image's layout (fast accel, eased
  // settle); when settled they hover a few pixels, slowly and independently.

  AsciiBackground.prototype._dmEnsure = function () {
    if (this._dmReady) { return; }
    var N = this._dmN = 1500; // particle pool cap
    var P = this.particles = [];
    for (var i = 0; i < N; i++) {
      P.push({
        x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0, ch: ' ', fs: 10,
        r: 0, g: 0, b: 0, alpha: 0, active: false, ta: 0,
        hax: 0.25 + 0.75 * Math.random(),       // every dot drifts a little, with variety
        hay: 0.25 + 0.75 * Math.random(),
        hfx: 0.15 + Math.random() * 0.7, hfy: 0.15 + Math.random() * 0.7, // slow, gentle
        hpx: Math.random() * 6.283, hpy: Math.random() * 6.283,
        hmx: Math.random() * 6.283, hmy: Math.random() * 6.283
      });
    }
    this._dmReady = true;
  };

  // Build up to N glyph targets (pixel position + char + colour) for a frame.
  AsciiBackground.prototype._dmTargets = function (frame) {
    var ramp = this.cfg.charset, rampMax = ramp.length - 1, invert = this.cfg.invert;
    var cols = frame.cols, rows = frame.rows, cw = frame.cellW, ch = frame.cellH;
    var col = frame.color, out = [];
    for (var y = 0; y < rows; y++) {
      for (var x = 0; x < cols; x++) {
        var idx = y * cols + x;
        var ink = invert ? 1 - frame.bright[idx] : frame.bright[idx];
        if (ink <= 0.06) { continue; }
        var glyph = ramp.charAt(Math.round(Math.min(1, ink) * rampMax));
        if (glyph === ' ') { continue; }
        out.push({
          x: x * cw, y: y * ch, ch: glyph, fs: ch,
          r: col ? col[idx * 3] : 0, g: col ? col[idx * 3 + 1] : 0, b: col ? col[idx * 3 + 2] : 0
        });
      }
    }
    // even-subsample down to the pool size (keeps raster order for coherence)
    if (out.length > this._dmN) {
      var step = out.length / this._dmN, thin = [];
      for (var k = 0; k < this._dmN; k++) { thin.push(out[Math.floor(k * step)]); }
      return thin;
    }
    return out;
  };

  // Point the particles at a frame's targets. seed=true snaps them into place
  // (used on first paint / resize); seed=false lets them fly from where they are.
  AsciiBackground.prototype._dmRetarget = function (idx, seed) {
    this._dmEnsure();
    var frame = this.frames[idx];
    if (!frame) { return; }
    var T = this._dmTargets(frame), P = this.particles, N = this._dmN;
    for (var i = 0; i < N; i++) {
      var p = P[i];
      if (i < T.length) {
        var t = T[i];
        if (!p.active && !seed) { // (re)entering — fly in from a random spot
          p.x = Math.random() * this.cssW; p.y = Math.random() * this.cssH; p.vx = 0; p.vy = 0;
        }
        p.tx = t.x; p.ty = t.y; p.ch = t.ch; p.fs = t.fs; p.r = t.r; p.g = t.g; p.b = t.b;
        p.active = true; p.ta = 1;
        if (seed) { p.x = t.x; p.y = t.y; p.vx = 0; p.vy = 0; p.alpha = 1; }
      } else {
        p.active = false; p.ta = 0; // extra particles fade away
      }
    }
  };

  AsciiBackground.prototype._dmStep = function (now) {
    this._dmEnsure();
    if (this._dmDirty || !this._dmSeeded) { this._dmRetarget(this.current || 0, true); this._dmDirty = false; this._dmSeeded = true; }

    var ctx = this.ctx, P = this.particles;
    var mono = this.cfg.colorMode !== 'source';
    var ff = this.cfg.fontFamily;
    var spd = this.cfg.animationSpeed || 1;
    var sp = Math.max(0.3, Math.min(1.6, spd));      // spring speed (clamped)
    // soft spring + extra friction: a wispy glide that eases in without
    // overshooting (no rubber-banding)
    var stiff = 0.045 * sp, damp = 0.72;
    var hoverMax = 1.5 + (this.cfg.animationAmount || 0.15) * 7; // gentle idle drift, a few px
    var tt = (now / 1000) * spd;
    var bold = Math.max(0, Math.min(1, (this.cfg.boldness || 0) / 100));

    ctx.textBaseline = 'top';
    if (bold > 0) { ctx.lineJoin = 'round'; ctx.lineCap = 'round'; }
    if (mono) { ctx.fillStyle = this.cfg.glyphColor; if (bold > 0) { ctx.strokeStyle = this.cfg.glyphColor; } }
    var lastFs = -1;

    // ---- interactive reactivity ------------------------------------------
    var repelOn = this._reactive && this.cfg.reactRepel && this.pointerIn;
    var attractOn = this._reactive && this.cfg.reactAttract && this.pointerIn;
    var shiverOn = this._reactive && this.cfg.reactShiver && this.pointerIn;
    var rRad = this.cfg.reactRadius || 160;
    var rStr = (this.cfg.reactStrength != null ? this.cfg.reactStrength : 0.6);
    var mx = this.mx, my = this.my;
    // click ripples scatter the particles once (they spring back afterwards)
    if (this._reactive && this.cfg.reactRipple && this._ripples.length) {
      for (var ci = 0; ci < this._ripples.length; ci++) {
        var cr = this._ripples[ci]; if (cr.dm) { continue; } cr.dm = true;
        var cReach = rRad * 1.6;
        for (var cj = 0; cj < P.length; cj++) {
          var cp = P[cj]; if (!cp.active) { continue; }
          var cdx = cp.x - cr.x, cdy = cp.y - cr.y, cdl = Math.sqrt(cdx * cdx + cdy * cdy) || 1;
          if (cdl < cReach) {
            var imp = (1 - cdl / cReach) * (6 + rStr * 30);
            cp.vx += (cdx / cdl) * imp; cp.vy += (cdy / cdl) * imp;
          }
        }
      }
    }

    for (var i = 0; i < P.length; i++) {
      var p = P[i];
      p.alpha += (p.ta - p.alpha) * 0.12;
      if (p.alpha < 0.02 && p.ta === 0) { continue; }

      // gentle, slow, independent hover — amplitude itself drifts so dots are
      // not always moving
      var hx = 0, hy = 0;
      if (!this.reduced) {
        var amx = 0.5 + 0.5 * Math.sin(tt * 0.15 + p.hmx);
        var amy = 0.5 + 0.5 * Math.sin(tt * 0.17 + p.hmy);
        hx = Math.sin(tt * p.hfx + p.hpx) * p.hax * hoverMax * amx;
        hy = Math.sin(tt * p.hfy + p.hpy) * p.hay * hoverMax * amy;
      }
      // spring toward target: accelerates while far, eases in as it arrives
      p.vx = (p.vx + ((p.tx + hx) - p.x) * stiff) * damp; p.x += p.vx;
      p.vy = (p.vy + ((p.ty + hy) - p.y) * stiff) * damp; p.y += p.vy;

      // reactive cursor repel: nudge the particle out of the pointer's bubble
      if (repelOn) {
        var rdx = p.x - mx, rdy = p.y - my, rdl = Math.sqrt(rdx * rdx + rdy * rdy);
        if (rdl < rRad && rdl > 0.01) {
          var rf = (1 - rdl / rRad); rf = rf * rf * (8 + rStr * 22);
          p.x += (rdx / rdl) * rf; p.y += (rdy / rdl) * rf;
        }
      }
      // reactive cursor attract: pull the particle toward the pointer (magnet)
      if (attractOn) {
        var adx = p.x - mx, ady = p.y - my, adl = Math.sqrt(adx * adx + ady * ady);
        if (adl < rRad && adl > 0.01) {
          var afp = (1 - adl / rRad); afp = afp * afp * (rStr * 27);
          p.x -= (adx / adl) * afp; p.y -= (ady / adl) * afp;
        }
      }
      // reactive shiver: particles near the pointer vibrate each frame
      if (shiverOn) {
        var sdx = p.x - mx, sdy = p.y - my, sdl = Math.sqrt(sdx * sdx + sdy * sdy);
        if (sdl < rRad) {
          var sfp = (1 - sdl / rRad) * rStr * (p.fs || 10) * 0.5;
          p.x += (hash(i, 0, Math.floor(tt * 40)) - 0.5) * sfp;
          p.y += (hash(i, 7, Math.floor(tt * 40)) - 0.5) * sfp;
        }
      }

      if (p.ch === ' ') { continue; }
      if (p.fs !== lastFs) { ctx.font = (p.fs * 0.92) + 'px ' + ff; lastFs = p.fs; if (bold > 0) { ctx.lineWidth = bold * p.fs * 0.16; } }
      ctx.globalAlpha = p.alpha;
      if (!mono) {
        var rgb = 'rgb(' + (p.r | 0) + ',' + (p.g | 0) + ',' + (p.b | 0) + ')';
        ctx.fillStyle = rgb; if (bold > 0) { ctx.strokeStyle = rgb; }
      }
      ctx.fillText(p.ch, p.x, p.y);
      if (bold > 0) { ctx.strokeText(p.ch, p.x, p.y); }
    }
    ctx.globalAlpha = 1;
  };

  AsciiBackground.prototype.destroy = function () {
    this._stop();
    window.removeEventListener('resize', this._boundResize);
    document.removeEventListener('visibilitychange', this._boundVisibility);
    if (this._io) { this._io.disconnect(); }
    if (this._boundPointerMove) { window.removeEventListener('pointermove', this._boundPointerMove); }
    if (this._boundPointerDown) { window.removeEventListener('pointerdown', this._boundPointerDown); }
    if (this._boundScroll) { window.removeEventListener('scroll', this._boundScroll); }
    if (this._boundWheel) { window.removeEventListener('wheel', this._boundWheel); }
    if (this.canvas && this.canvas.parentNode) { this.canvas.parentNode.removeChild(this.canvas); }
  };

  // ---- bootstrap ----------------------------------------------------------
  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-ascii-bg]');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].__asciiBg) { nodes[i].__asciiBg = new AsciiBackground(nodes[i]); }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Shopify theme editor: re-init when the section is loaded/changed
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-ascii-bg]');
    if (el && el.__asciiBg) { el.__asciiBg.destroy(); el.__asciiBg = null; }
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-ascii-bg]');
    if (el && el.__asciiBg) { el.__asciiBg.destroy(); el.__asciiBg = null; }
  });

  window.AsciiBackground = AsciiBackground;
})();
